// Workflows Store - Gestión de workflows con Zustand
import { create } from 'zustand';
import { workflowsService, type Workflow, type CreateWorkflowInput, type UpdateWorkflowInput } from '../lib/workflowsService';
import { withToast, ToastMessages } from '../lib/toastHelper';
import { useExcelFilesStore } from './excelFilesStore';
import type { Node, Edge } from '@xyflow/react';

export interface ImportedWorkflowDraft {
  draftId: string;
  name: string;
  description?: string;
  nodes: Node[];
  edges: Edge[];
  excelFiles?: Workflow['excelFiles'];
}

interface WorkflowsStore {
  workflows: Workflow[];
  currentWorkflow: Workflow | null;
  importDraft: ImportedWorkflowDraft | null;
  loading: boolean;
  error: string | null;
  fetchWorkflows: (userId: string) => Promise<void>;
  fetchWorkflow: (id: string) => Promise<void>;
  createWorkflow: (userId: string, input: CreateWorkflowInput) => Promise<string>;
  updateWorkflow: (id: string, input: UpdateWorkflowInput, skipRefetch?: boolean) => Promise<void>;
  deleteWorkflow: (id: string) => Promise<void>;
  saveWorkflow: (id: string | undefined, userId: string, nodes: Node[], edges: Edge[], name: string, description?: string) => Promise<string>;
  setImportDraft: (draft: Omit<ImportedWorkflowDraft, 'draftId'>) => void;
  clearImportDraft: () => void;
  clearCurrentWorkflow: () => void;
  clearError: () => void;
}

export const useWorkflowsStore = create<WorkflowsStore>((set, get) => ({
  workflows: [],
  currentWorkflow: null,
  importDraft: null,
  loading: false,
  error: null,

  fetchWorkflows: async (userId: string) => {
    try {
      set({ loading: true, error: null });
      const workflows = await workflowsService.getUserWorkflows(userId);
      set({ workflows, loading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al cargar workflows';
      set({ loading: false, error: message });
    }
  },

  fetchWorkflow: async (id: string) => {
    try {
      set({ loading: true, error: null });
      const workflow = await workflowsService.getWorkflow(id);
      set({ currentWorkflow: workflow, loading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al cargar workflow';
      set({ loading: false, error: message });
    }
  },

  createWorkflow: async (userId: string, input: CreateWorkflowInput) => {
    return withToast(
      async () => {
        set({ loading: true, error: null });
        try {
          const id = await workflowsService.createWorkflow(userId, input);
          await get().fetchWorkflows(userId);
          set({ loading: false });
          return id;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Error al crear workflow';
          set({ loading: false, error: message });
          throw error;
        }
      },
      ToastMessages.workflows.create
    );
  },

  updateWorkflow: async (id: string, input: UpdateWorkflowInput, skipRefetch?: boolean) => {
    return withToast(
      async () => {
        set({ loading: true, error: null });
        try {
          await workflowsService.updateWorkflow(id, input);
          const { currentWorkflow } = get();
          if (currentWorkflow?.id === id) {
            // H-F014: Filtrar campos undefined del input antes del spread
            // para no sobreescribir campos existentes con undefined
            const filteredInput: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(input)) {
              if (value !== undefined) {
                filteredInput[key] = value;
              }
            }
            set({
              currentWorkflow: { ...currentWorkflow, ...filteredInput } as Workflow,
              loading: false
            });
          }
          // Permitir omitir refetch cuando el llamador (saveWorkflow) lo hara despues
          if (!skipRefetch) {
            const userId = currentWorkflow?.userId || '';
            if (userId) {
              await get().fetchWorkflows(userId);
            }
          } else {
            set({ loading: false });
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Error al actualizar workflow';
          set({ loading: false, error: message });
          throw error;
        }
      },
      ToastMessages.workflows.update
    );
  },

  deleteWorkflow: async (id: string) => {
    return withToast(
      async () => {
        set({ loading: true, error: null });
        try {
          await workflowsService.deleteWorkflow(id);
          const { workflows, currentWorkflow } = get();
          const userId = currentWorkflow?.userId || workflows.find(w => w.id === id)?.userId;
          
          set({ 
            workflows: workflows.filter(w => w.id !== id),
            currentWorkflow: currentWorkflow?.id === id ? null : currentWorkflow,
            loading: false 
          });
          
          // Recargar workflows para actualizar la lista
          if (userId) {
            await get().fetchWorkflows(userId);
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Error al eliminar workflow';
          set({ loading: false, error: message });
          throw error;
        }
      },
      ToastMessages.workflows.delete
    );
  },

  saveWorkflow: async (id: string | undefined, userId: string, nodes: Node[], edges: Edge[], name: string, description?: string) => {
    if (import.meta.env.DEV) console.log('[SaveFlow:store] saveWorkflow llamado. id:', id, 'name:', name);
    // Obtener archivos Excel globales del store (import estatico, sin dependencia circular)
    const excelFiles = useExcelFilesStore.getState().files;
    // [seguridad H-F002] No persistir filePath (ruta absoluta del agente) en Firestore.
    // Solo conservar relativePath para re-sincronizar en futuras sesiones.
    const excelFilesData = excelFiles.map((file) => ({
      id: file.id,
      name: file.name,
      relativePath: file.relativePath,
      localPath: file.localPath,
      headers: file.headers,
      totalRows: file.totalRows,
      firstRowAsHeaders: file.firstRowAsHeaders,
      delimiter: file.delimiter,
    }));

    if (id) {
      if (import.meta.env.DEV) console.log('[SaveFlow:store] Actualizando workflow existente:', id);
      // H-F059: Flujo de guardado para workflows existentes:
      // 1. updateWorkflow con skipRefetch=true (evita doble fetchWorkflows)
      // 2. fetchWorkflows para actualizar la lista del Dashboard con los datos mas recientes de Firestore
      await get().updateWorkflow(id, { nodes, edges, name, description, excelFiles: excelFilesData }, true);
      await get().fetchWorkflows(userId);
      return id;
    } else {
      if (import.meta.env.DEV) console.log('[SaveFlow:store] Creando workflow nuevo...');
      // createWorkflow internamente hace fetchWorkflows, no necesita llamada adicional
      const workflowId = await get().createWorkflow(userId, { nodes, edges, name, description, excelFiles: excelFilesData });
      if (import.meta.env.DEV) console.log('[SaveFlow:store] Workflow creado. ID:', workflowId);
      return workflowId;
    }
  },

  setImportDraft: (draft) => {
    set({
      importDraft: {
        ...draft,
        draftId: `import-${Date.now()}`,
      },
    });
  },

  clearImportDraft: () => {
    set({ importDraft: null });
  },

  clearCurrentWorkflow: () => {
    set({ currentWorkflow: null });
  },

  clearError: () => {
    set({ error: null });
  },
}));
